/**
 * `<BasicControlsDialog>` — "[Brand model] uses basic controls" notice (D1, ENG-4925).
 *
 * Shown once per add when the camera will run on the generic driver, however
 * it was chosen (picked from the list or recognised by discovery). Board D
 * (Travis, 2026-10-23) fixes the shape: the title names the camera, one
 * Continue button keeps it, and the header X returns to the brand/model step.
 * The "isn't listed" / "isn't supported yet" dialogs (C1/C2) already explain
 * the driver, so that path never reaches D1 (see `shouldShowBasicControls`).
 * Copy names the product through `vendorName()` so Hive builds read correctly.
 */
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { createDeviceIdentificationTracker } from '#~/analytics/domains/device-identification.js';
import * as _Footer from '#~/common/editor/footer/index.js';
import { Sel, STagNew } from '#~/stag/tag.js';
import * as Dialog from '@studio/design-system/dialog';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { ReactElement } from 'react';
import { basicControlsCameraLabel } from './utils.js';

// Re-exported: the label rule is shared with the multi-add list (bulk.ts).
export { basicControlsCameraLabel };

export interface BasicControlsDialogProps {
  open: boolean;
  /** Camera named in the title, e.g. "AVer PTZ310" (see `basicControlsCameraLabel`). */
  cameraLabel: string;
  /** Registry brand name, reported on `device_basic_controls_closed`. */
  brandName: string;
  /** Registry model name, reported on `device_basic_controls_closed`. */
  modelName: string;
  /** Continue: keep the camera and carry on. */
  onContinue: () => void;
  /** Header X (or Escape / outside click): return to the brand/model step. */
  onClose: () => void;
}

/**
 * Board D's body copy, in the app's own name (Iris / Hive), for one camera or
 * several (the multi-add list, ENG-4942).
 */
export const basicControlsBody = (count: number): string =>
  count === 1
    ? `${vendorName()} controls this camera with a standard driver. You can move and zoom it, but Click to Center and Fast Frame may not work with it yet.`
    : `${vendorName()} controls these cameras with a standard driver. You can move and zoom them, but Click to Center and Fast Frame may not work with them yet.`;

/** `<BasicControlsDialog>` component. */
export const BasicControlsDialog = ({
  open,
  cameraLabel,
  brandName,
  modelName,
  onContinue,
  onClose
}: BasicControlsDialogProps): ReactElement | null => {
  const { track } = useAnalytics();
  const tracker = createDeviceIdentificationTracker(track);

  if (!open) return null;

  const handleContinue = (): void => {
    tracker.trackDeviceBasicControlsClosed({
      brand_name: brandName,
      model_name: modelName,
      outcome: 'continue'
    });
    onContinue();
  };

  const handleClose = (): void => {
    tracker.trackDeviceBasicControlsClosed({
      brand_name: brandName,
      model_name: modelName,
      outcome: 'closed'
    });
    onClose();
  };

  return (
    // The header X, Escape and outside click share Radix's onOpenChange(false):
    // all of them are "closed" (back to the brand/model step), per Board D.
    <Dialog.Dialog open onOpenChange={(nextOpen) => !nextOpen && handleClose()}>
      <Dialog.Overlay>
        <Dialog.Content className='fixed left-1/2 top-1/2 flex w-[506px] -translate-x-1/2 -translate-y-1/2 flex-col rounded-12 bg-base-200 shadow-lg'>
          <Dialog.Header title={`${cameraLabel} uses basic controls`} />

          <div className='px-40 py-24'>
            <p className='text-sm text-muted'>{basicControlsBody(1)}</p>
          </div>

          <Dialog.Footer>
            <_Footer.Next
              onClick={handleContinue}
              {...STagNew(Sel.AddDeviceIdentification, 'basic-controls-continue')}
            />
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog.Dialog>
  );
};
